import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from '@zurich-primitives/button';
import { Link } from '@zurich-data-dsm-icons/action.link';
import {
  getBusinessArea,
  getService,
  setBusinessArea,
  setCurrentFile,
  useAppSelector,
  setDisclaimerModal,
  setEmailChain,
  getEmailChain,
  setGeneratingExtraction
} from 'app/redux';
import { IBusinessArea, handleFileValidation } from 'app/utils';
import config from 'config';
import BAMapping from 'data/mock/businessAreaAndServices.json';
import { ReactComponent as AttachIcon } from 'app/assets/Icons/AttachWhite.svg';
import { ReactComponent as MailIcon } from 'app/assets/Icons/Chips/Mail.svg';
import { ReactComponent as NavigateIcon } from 'app/assets/Icons/Send.svg';
import styles from './LetsTry.module.scss';
import UploadDocumentModal from 'app/components/Modals/UploadDocumentModal/UploadDocumentModal';
import {
  SERVICE_NAMES,
  noRenderUploadFileButton,
  noSummaryExtractionServices
} from 'app/utils/consts';
import { getAllowedMimeTypes } from 'app/utils/functions';

export const LetsTry: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentService = useAppSelector(getService);
  const businessArea = useAppSelector(getBusinessArea);
  const currentEmailChain = useAppSelector(getEmailChain);
  const businessAreas: IBusinessArea = BAMapping.businessAreas;
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const useModalState = (initial: boolean) => {
    const [open, setOpen] = useState<boolean>(initial);
    return {
      isOpen: open,
      open: () => setOpen(true),
      close: () => setOpen(false),
      set: setOpen
    };
  };
  const modal = useModalState(false);

  const handleOpenAPI = (): void => {
    if (currentService) {
      navigate(config.APP.PATH.API.replace(':api', String(currentService)));
    } else {
      dispatch(setDisclaimerModal(true));
    }
  };

  const getUndefinedBA = (): void => {
    if (currentService !== undefined && businessArea === undefined) {
      for (const property in businessAreas) {
        if (businessAreas[property].services.includes(String(currentService)))
          dispatch(setBusinessArea(property));
      }
    }
  };

  const handleFileChange = (file: File): void => {
    const validation = handleFileValidation(file, currentService);
    if (validation.valid) {
      if (businessArea == null) getUndefinedBA();
      dispatch(setGeneratingExtraction(true));
      dispatch(setCurrentFile(file));
      navigate(config.APP.PATH.SUMMARY);
    } else {
      const errorMessage = validation.error;
      console.error(errorMessage);
      setErrorText(errorMessage);
    }
  };

  const handleEmailSummaryChange = (value: string): void => {
    dispatch(setEmailChain(value));
  };

  const handleNavigateToSummary = (): void => {
    if (businessArea == null) getUndefinedBA();
    navigate(config.APP.PATH.SUMMARY);
  };

  const handleSelectionClick = (): void => {
    if (businessArea == null) getUndefinedBA();
    if (currentService === undefined) {
      dispatch(setDisclaimerModal(true));
    } else {
      setErrorText(undefined);
      modal.open();
    }
  };

  useEffect(() => setErrorText(undefined), [currentService]);

  const renderUploadFileButton = (): React.JSX.Element => (
    <div className="attach-btn">
      <Button onClick={handleSelectionClick}>
        <Button.Icon>
          <AttachIcon />
        </Button.Icon>
        <Button.Label>Upload File</Button.Label>
      </Button>
      <span className={styles.error}>{errorText}</span>
    </div>
  );

  const renderCallSummaryButton = (): React.JSX.Element =>
    renderUploadFileButton();

  const renderClaimsEmailSummaryButton = (): React.JSX.Element => (
    <Button
      onClick={handleNavigateToSummary}
      disabled={currentEmailChain == null || currentEmailChain?.length === 0}
    >
      <Button.Icon>
        <MailIcon />
      </Button.Icon>
      <Button.Label>Summarize email</Button.Label>
    </Button>
  );

  const renderLetsTryItButton = (): React.JSX.Element => (
    <Button onClick={handleNavigateToSummary}>
      <Button.Icon>
        <NavigateIcon />
      </Button.Icon>
      <Button.Label>Let&apos;s try it</Button.Label>
    </Button>
  );

  return (
    <div className={styles['lets-try']} id="letstry">
      <h3 className={styles['heading-3']}>Let&apos;s try it</h3>
      {currentService &&
        !noSummaryExtractionServices.includes(currentService) &&
        currentService !== SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY && (
          <p>
            To get started, simply choose your business area and the service
            type that you&apos;d like to explore and give it a try.
            <br />
            Just remember to keep the file size below 10 MB.
          </p>
        )}
      {currentService === SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY && (
        <div className={styles['email-summary-form']}>
          <textarea
            onChange={(e) => handleEmailSummaryChange(e.target.value)}
            placeholder="Please copy and paste your claims email here"
          />
        </div>
      )}
      <div className={styles.buttons}>
        {currentService === SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY &&
          renderClaimsEmailSummaryButton()}
        {currentService === SERVICE_NAMES.CALL_SUMMARY &&
          renderCallSummaryButton()}
        {currentService &&
          noSummaryExtractionServices.includes(currentService) &&
          renderLetsTryItButton()}
        {!noRenderUploadFileButton.includes(currentService!) &&
          renderUploadFileButton()}
        <Button onClick={handleOpenAPI}>
          <Button.Icon>
            <Link />
          </Button.Icon>
          <Button.Label>Use our API version</Button.Label>
        </Button>
      </div>

      <UploadDocumentModal
        isOpen={modal.isOpen}
        onClose={() => modal.close()}
        onUpload={(file) => {
          handleFileChange(file);
          modal.close();
        }}
        acceptedFileTypes={getAllowedMimeTypes(currentService)}
        title={'Upload Files'}
        showLanguageSelector={currentService === SERVICE_NAMES.CALL_SUMMARY}
      />
    </div>
  );
};
